import React, { useCallback, useEffect, useRef, useState } from "react";
import { Row, Col } from "reactstrap";
import { TextField, InputAdornment, AlertColor, Tooltip } from "@mui/material";
import PrimaryButton from "../../../../../../../toolkit/primaryButton";
import AnimatedErrorMessage from "../../../../../../../toolkit/animatedErrorMessage";
import SectionLabel from "../../../../onboarding/onboardingCustomation/components/leftSideBar/common/sectionLabel";
import TooltipColorPicker from "../../../../onboarding/onboardingCustomation/components/leftSideBar/common/tooltipColorPicker";
import FormFieldRow from "../../../organizationSetup/components/FormFieldRow";
import {
  fetchCharityInfo,
  saveCharityInfo,
  setCustomDonationUrl,
  setHighlightColor,
} from "../../../../../../../utils/fetch";
import IGlobalConfig from "../../../../../../../types/IGlobalConfig";
import { saveBrandSetupData } from "../../../../../../../utils/firebase/brandSetup";
import {
  isValidUrlValue,
  validateRequiredUrl,
} from "../../../../../../../utils/validation/validationUtils";
import s from "../styles.scss";

const DEFAULT_EMAIL_HIGHLIGHT_COLOR = "#0B4F8D";
const HEX_COLOR_PATTERN = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

const wizardTextFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#fff",
    fontSize: "15px",
    fontFamily: '"Inter", sans-serif',
    "& fieldset": {
      borderColor: "#E2E8F0",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: "#CBD5E1",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0078D4",
      borderWidth: "1px",
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "16px 14px",
    fontSize: "15px",
    fontFamily: '"Inter", sans-serif',
    color: "#334155",
    fontWeight: 500,
    letterSpacing: "-0.01em",
    "&::placeholder": {
      color: "#8A99AE",
      opacity: 1,
    },
  },
  "& .MuiInputAdornment-root": {
    color: "#64748B",
  },
};

interface GeneralSettingsPanelProps {
  charityId: string;
  config?: IGlobalConfig;
  serviceAccessToken?: string;
  configHighlightColor?: string | null;
  customDonationUrl?: string | null;
  showExternalDonationUrl?: boolean;
  showSnackbar?: (message: string, severity?: AlertColor) => void;
}

const GeneralSettingsPanel: React.FC<GeneralSettingsPanelProps> = ({
  charityId,
  config,
  serviceAccessToken,
  configHighlightColor,
  customDonationUrl,
  showExternalDonationUrl = false,
  showSnackbar,
}) => {
  const [highlightColor, setHighlightColorValue] = useState(DEFAULT_EMAIL_HIGHLIGHT_COLOR);
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState("");
  const [termsAndConditionsUrl, setTermsAndConditionsUrl] = useState("");
  const [donationUrl, setDonationUrl] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [privacyPolicyError, setPrivacyPolicyError] = useState<string | null>(null);
  const [termsAndConditionsError, setTermsAndConditionsError] = useState<string | null>(null);
  const [donationUrlError, setDonationUrlError] = useState<string | null>(null);
  const savedRef = useRef({
    highlightColor: DEFAULT_EMAIL_HIGHLIGHT_COLOR,
    privacyPolicyUrl: "",
    termsAndConditionsUrl: "",
    donationUrl: "",
  });
  const charityInfoRef = useRef<Record<string, unknown> | null>(null);

  useEffect(() => {
    const fromConfig = configHighlightColor?.trim();
    const nextHighlight =
      fromConfig && HEX_COLOR_PATTERN.test(fromConfig)
        ? fromConfig
        : DEFAULT_EMAIL_HIGHLIGHT_COLOR;
    setHighlightColorValue(nextHighlight);
    savedRef.current.highlightColor = nextHighlight;
  }, [configHighlightColor]);

  useEffect(() => {
    const nextDonationUrl = customDonationUrl?.trim() || "";
    setDonationUrl(nextDonationUrl);
    savedRef.current.donationUrl = nextDonationUrl;
  }, [customDonationUrl]);

  useEffect(() => {
    if (!charityId || !config?.adminServiceUrl || !serviceAccessToken) {
      return;
    }

    let cancelled = false;
    fetchCharityInfo(config.adminServiceUrl, charityId, serviceAccessToken)
      .then(info => {
        if (cancelled || !info) {
          return;
        }
        charityInfoRef.current = info;
        const privacy = info.privacyPolicyUrl?.trim() || "";
        const terms = info.termsAndConditionsUrl?.trim() || "";
        setPrivacyPolicyUrl(privacy);
        setTermsAndConditionsUrl(terms);
        savedRef.current.privacyPolicyUrl = privacy;
        savedRef.current.termsAndConditionsUrl = terms;
      })
      .catch(error => {
        console.error("Failed to load charity info:", error);
        showSnackbar?.("Unable to load other settings.", "error");
      });

    return () => {
      cancelled = true;
    };
  }, [charityId, config?.adminServiceUrl, serviceAccessToken, showSnackbar]);

  const handleSave = useCallback(async () => {
    const normalizedHighlight = highlightColor.trim();
    const normalizedPrivacy = privacyPolicyUrl.trim();
    const normalizedTerms = termsAndConditionsUrl.trim();
    const normalizedDonation = donationUrl.trim();

    const nextPrivacyError = validateRequiredUrl(normalizedPrivacy, "Privacy Policy URL");
    const nextTermsError = validateRequiredUrl(
      normalizedTerms,
      "Terms and Conditions URL"
    );
    const nextDonationError =
      showExternalDonationUrl && normalizedDonation && !isValidUrlValue(normalizedDonation)
        ? "Please enter a valid URL (e.g., example.com or https://example.com)"
        : null;

    setPrivacyPolicyError(nextPrivacyError);
    setTermsAndConditionsError(nextTermsError);
    setDonationUrlError(nextDonationError);

    if (!HEX_COLOR_PATTERN.test(normalizedHighlight)) {
      showSnackbar?.("Enter a valid highlight color (for example #0B4F8D).", "error");
      return;
    }
    if (nextPrivacyError || nextTermsError || nextDonationError) {
      return;
    }

    if (!config?.adminServiceUrl || !serviceAccessToken) {
      showSnackbar?.("Unable to save settings. Please try again.", "error");
      return;
    }

    setIsSaving(true);
    try {
      const highlightChanged = normalizedHighlight !== savedRef.current.highlightColor;
      const privacyChanged = normalizedPrivacy !== savedRef.current.privacyPolicyUrl;
      const termsChanged = normalizedTerms !== savedRef.current.termsAndConditionsUrl;
      const donationChanged =
        showExternalDonationUrl &&
        normalizedDonation !== savedRef.current.donationUrl;

      if (!highlightChanged && !privacyChanged && !termsChanged && !donationChanged) {
        return;
      }

      if (highlightChanged) {
        await setHighlightColor(
          charityId,
          config.adminServiceUrl,
          serviceAccessToken,
          normalizedHighlight
        );
        savedRef.current.highlightColor = normalizedHighlight;
      }

      if (privacyChanged || termsChanged) {
        const baseInfo = charityInfoRef.current || {};
        await saveCharityInfo(
          config.adminServiceUrl,
          charityId,
          serviceAccessToken,
          {
            ...baseInfo,
            privacyPolicyUrl: normalizedPrivacy,
            termsAndConditionsUrl: normalizedTerms,
          }
        );
        savedRef.current.privacyPolicyUrl = normalizedPrivacy;
        savedRef.current.termsAndConditionsUrl = normalizedTerms;
      }

      if (donationChanged) {
        await setCustomDonationUrl(
          charityId,
          config.adminServiceUrl,
          serviceAccessToken,
          normalizedDonation
        );
        savedRef.current.donationUrl = normalizedDonation;
      }

      await saveBrandSetupData(charityId, { generalSettingsSaved: true });
      showSnackbar?.("Other settings saved.", "success");
    } catch (error) {
      console.error("Failed to save general settings:", error);
      showSnackbar?.("Failed to save other settings. Please try again.", "error");
    } finally {
      setIsSaving(false);
    }
  }, [
    charityId,
    config?.adminServiceUrl,
    donationUrl,
    highlightColor,
    privacyPolicyUrl,
    serviceAccessToken,
    showExternalDonationUrl,
    showSnackbar,
    termsAndConditionsUrl,
  ]);

  const hasChanges =
    highlightColor.trim() !== savedRef.current.highlightColor ||
    privacyPolicyUrl.trim() !== savedRef.current.privacyPolicyUrl ||
    termsAndConditionsUrl.trim() !== savedRef.current.termsAndConditionsUrl ||
    (showExternalDonationUrl &&
      donationUrl.trim() !== savedRef.current.donationUrl);

  return (
    <>
      <FormFieldRow spacing="first">
        <div className={s.generalSettingsColorField}>
          <SectionLabel
            label="Highlight Color"
            tooltip="Used as the highlight color in emails"
          />
          <TooltipColorPicker
            value={highlightColor}
            setValue={setHighlightColorValue}
            maxInputWidth="160px"
          />
        </div>
      </FormFieldRow>

      <FormFieldRow>
        <div>
          <Tooltip title="Link to your organization’s privacy policy page" placement="top">
            <TextField
              fullWidth
              type="url"
              value={privacyPolicyUrl}
              onChange={event => {
                setPrivacyPolicyUrl(event.target.value);
                if (privacyPolicyError) {
                  setPrivacyPolicyError(null);
                }
              }}
              placeholder="Privacy Policy URL"
              error={!!privacyPolicyError}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start" sx={{ ml: 0.5 }}>
                      <i className="fa-solid fa-shield-halved" style={{ color: "#64748B", fontSize: "18px" }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={wizardTextFieldSx}
            />
          </Tooltip>
          <AnimatedErrorMessage
            show={!!privacyPolicyError}
            message={privacyPolicyError || undefined}
            variant="form"
          />
        </div>
      </FormFieldRow>

      <FormFieldRow>
        <div>
          <Tooltip title="Link to your organization’s terms and conditions page" placement="top">
            <TextField
              fullWidth
              type="url"
              value={termsAndConditionsUrl}
              onChange={event => {
                setTermsAndConditionsUrl(event.target.value);
                if (termsAndConditionsError) {
                  setTermsAndConditionsError(null);
                }
              }}
              placeholder="Terms and Conditions URL"
              error={!!termsAndConditionsError}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start" sx={{ ml: 0.5 }}>
                      <i className="fa-solid fa-file-contract" style={{ color: "#64748B", fontSize: "18px" }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={wizardTextFieldSx}
            />
          </Tooltip>
          <AnimatedErrorMessage
            show={!!termsAndConditionsError}
            message={termsAndConditionsError || undefined}
            variant="form"
          />
        </div>
      </FormFieldRow>

      {showExternalDonationUrl && (
        <FormFieldRow>
          <div>
            <Tooltip title="External page where donors go to make a donation" placement="top">
              <TextField
                fullWidth
                type="url"
                value={donationUrl}
                onChange={event => {
                  setDonationUrl(event.target.value);
                  if (donationUrlError) {
                    setDonationUrlError(null);
                  }
                }}
                placeholder="Custom donation URL"
                error={!!donationUrlError}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start" sx={{ ml: 0.5 }}>
                        <i className="fa-solid fa-hand-holding-heart" style={{ color: "#64748B", fontSize: "18px" }} />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={wizardTextFieldSx}
              />
            </Tooltip>
            <AnimatedErrorMessage
              show={!!donationUrlError}
              message={donationUrlError || undefined}
              variant="form"
            />
          </div>
        </FormFieldRow>
      )}

      <Row className="mt-4 justify-content-center">
        <Col xs="auto">
          <PrimaryButton
            variant="primary"
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? "Saving..." : "Save settings"}
          </PrimaryButton>
        </Col>
      </Row>
    </>
  );
};

export default GeneralSettingsPanel;
